// src/pages/HistoryCompanyIntro.jsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Link as MLink,
} from "@mui/material";
import { alpha } from "@mui/material/styles";

/* ------------ language data ------------ */

const COMPANY_BY_LANG = {
  ZH: {
    title: "公司簡介",
    rows: [
      ["公司名稱", "台灣水禾實業有限公司"],
      ["地址", "台灣新北市林口區文明街183號"],
      ["創立時間", "西元2015年4月1日"],
      [
        "事業內容",
        [
          "1.衛生與健康商品和服務的研發、生產以及銷售。",
          "2.食品衛生和環境衛生的諮詢服務。",
          "3.病媒蚊防治等商品的研發、生產以及銷售。",
        ],
      ],
      ["電話", { text: "02-2609-6867", href: "tel:0226096867" }],
      ["傳真", { text: "02-2609-6837", href: "tel:0226096837" }],
      ["客服郵件", "service@sehopro.com"],
    ],
  },
  EN: {
    title: "Company Profile",
    rows: [
      ["Company Name", "Taiwan SEHO Industrial Co., Ltd."],
      [
        "Address",
        "No. 183, Wenming Street, Linkou District, New Taipei City, Taiwan",
      ],
      ["Established", "April 1, 2015"],
      [
        "Business Scope",
        [
          "1. R&D, production, and sales of hygiene and health products and services.",
          "2. Consulting services for food hygiene and environmental sanitation.",
          "3. R&D, production, and sales of pest and vector-control related products.",
        ],
      ],
      [
        "Telephone",
        { text: "+886-2-2609-6867", href: "tel:0226096867" },
      ],
      ["Fax", { text: "+886-2-2609-6837", href: "tel:0226096837" }],
      ["Service Email", "service@sehopro.com"],
    ],
  },
  JP: {
    title: "会社概要",
    rows: [
      ["会社名", "台灣水禾實業有限会社"],
      ["住所", "新北市林口区文明街183号"],
      ["設立日", "2015年4月1日"],
      [
        "事業内容",
        [
          "1. 衛生・健康関連製品およびサービスの研究開発・製造・販売",
          "2. 食品衛生および環境衛生に関するコンサルティング",
          "3. 蚊など病媒害虫防除関連製品の研究開発・製造・販売",
        ],
      ],
      ["電話番号", { text: "+886-2-2609-6867", href: "tel:0226096867" }],
      ["FAX", { text: "+886-2-2609-6837", href: "tel:0226096837" }],
      ["カスタマーサービスメール", "service@sehopro.com"],
    ],
  },
};

function getCompanyData(lang = "ZH") {
  return COMPANY_BY_LANG[lang] || COMPANY_BY_LANG.ZH;
}

export default function HistoryCompanyIntro({
  mode = "b2b",
  fonts,
  bg = "background.paper",
  rail,
  lang = "ZH", // <- ZH / EN / JP
}) {
  const accent = rail;
  const divider = alpha(accent, 0.35);
  const COMPANY = getCompanyData(lang);

  const rowGrid = {
    display: "grid",
    alignItems: "center",
    gridTemplateColumns: {
      xs: "minmax(92px, 36%) 1fr",
      sm: "minmax(110px, 32%) 1fr",
      md: "minmax(140px, 30%) 1fr",
    },
    columnGap: { xs: 1.5, md: 2 },
    py: { xs: 1.25, md: 1.5 },
    borderBottom: `1px solid ${alpha(accent, 0.25)}`,
  };

  const labelSx = {
    color: "text.primary",
    fontWeight: 600,
    lineHeight: 1.8,
    fontSize: { xs: "clamp(13px, 2.9vw, 14px)", md: 16 },
    pr: { xs: 1, md: 2 },
  };

  const valueTextSx = {
    color: accent,
    lineHeight: 1.9,
    fontFamily: fonts,
    fontSize: { xs: "clamp(14px, 3.4vw, 16px)", md: 16 },
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    minWidth: 0,
  };

  return (
    <Box component="section" sx={{ bgcolor: bg, py: { xs: 5, md: 6 } }}>
      <Container
        maxWidth={false}
        disableGutters
        sx={{ maxWidth: 960, px: { xs: 2, md: 3 }, mx: "auto" }}
      >
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontWeight: 700,
            fontSize: { xs: 22, md: 26 },
            fontFamily: fonts,
            letterSpacing: 1,
            color: accent,
            mb: { xs: 1.5, md: 3 },
          }}
        >
          {COMPANY.title}
        </Typography>

        <Box
          sx={{
            borderTop: `1px solid ${divider}`,
            borderBottom: `1px solid ${divider}`,
          }}
        >
          {COMPANY.rows.map(([label, value], i) => (
            <Box
              key={i}
              sx={{
                ...rowGrid,
                borderBottom:
                  i === COMPANY.rows.length - 1
                    ? "1px solid transparent"
                    : rowGrid.borderBottom,
              }}
            >
              {/* Label */}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={labelSx} fontFamily={fonts}>
                  {label}
                </Typography>
              </Box>

              {/* Value */}
              <Box sx={{ minWidth: 0 }}>
                {Array.isArray(value) ? (
                  label === "事業內容" ||
                  label === "Business Scope" ||
                  label === "事業内容" ? (
                    <Box sx={{ ...valueTextSx }}>
                      {value.map((v, idx) => (
                        <Typography
                          key={idx}
                          component="div"
                          sx={{ ...valueTextSx }}
                        >
                          {v}
                        </Typography>
                      ))}
                    </Box>
                  ) : (
                    <Stack
                      direction="row"
                      spacing={2}
                      useFlexGap
                      flexWrap="wrap"
                      sx={{
                        ...valueTextSx,
                        "& a": {
                          color: accent,
                          textDecorationColor: divider,
                        },
                        "& a:hover": { textDecoration: "underline" },
                      }}
                    >
                      {value.map((v, idx) =>
                        typeof v === "string" ? (
                          <Typography
                            key={idx}
                            component="span"
                            sx={valueTextSx}
                          >
                            {v}
                          </Typography>
                        ) : (
                          <MLink
                            key={idx}
                            href={v.href}
                            underline="hover"
                            sx={{ fontFamily: fonts }}
                          >
                            {v.text}
                          </MLink>
                        )
                      )}
                    </Stack>
                  )
                ) : typeof value === "object" && value?.href ? (
                  <MLink href={value.href} underline="hover" sx={valueTextSx}>
                    {value.text}
                  </MLink>
                ) : (
                  <Typography sx={valueTextSx}>{value}</Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
